/**
 * PWA Bid Period Helper
 * 
 * Matches the bid period definitions from utils/bid_period_helper.py
 * These are the official PWA contract bid periods, NOT calendar months.
 */

/**
 * Get PWA bid periods for a given year
 * @param {number} year - The year
 * @returns {Object} Bid periods with name, start, and end dates
 */
export const getBidPeriods = (year) => {
  return {
    "January": { start: new Date(year, 0, 1), end: new Date(year, 0, 30) },
    "February": { start: new Date(year, 0, 31), end: new Date(year, 2, 1) },
    "March": { start: new Date(year, 2, 2), end: new Date(year, 2, 31) },
    "April": { start: new Date(year, 3, 1), end: new Date(year, 4, 1) },
    "May": { start: new Date(year, 4, 2), end: new Date(year, 5, 1) },
    "June": { start: new Date(year, 5, 2), end: new Date(year, 6, 1) },
    "July": { start: new Date(year, 6, 2), end: new Date(year, 6, 31) },
    "August": { start: new Date(year, 7, 1), end: new Date(year, 7, 30) },
    "September": { start: new Date(year, 7, 31), end: new Date(year, 8, 30) },
    "October": { start: new Date(year, 9, 1), end: new Date(year, 9, 31) },
    "November": { start: new Date(year, 10, 1), end: new Date(year, 10, 30) },
    "December": { start: new Date(year, 11, 1), end: new Date(year, 11, 31) },
  };
};

/**
 * Get the current bid period based on today's date
 * @param {Date} targetDate - Optional date (defaults to now)
 * @returns {Object} { name, start, end, year }
 */
export const getCurrentBidPeriod = (targetDate = new Date()) => {
  const year = targetDate.getFullYear();
  const periods = getBidPeriods(year);
  
  // Normalize target date to start of day for comparison
  const target = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
  
  for (const [name, { start, end }] of Object.entries(periods)) {
    if (target >= start && target <= end) {
      return { name, start, end, year };
    }
  }
  
  // Check previous year's December (in case we're in late December spillover)
  const prevYearPeriods = getBidPeriods(year - 1);
  const dec = prevYearPeriods["December"];
  if (target >= dec.start && target <= dec.end) {
    return { name: "December", start: dec.start, end: dec.end, year: year - 1 };
  }
  
  // Fallback - shouldn't happen
  console.warn(`Date ${targetDate} does not fall within any bid period`);
  return null;
};

/**
 * Get bid period name formatted for display
 * @param {Date} targetDate - Optional date (defaults to now)
 * @returns {string} e.g., "January 2026"
 */
export const getCurrentBidPeriodName = (targetDate = new Date()) => {
  const period = getCurrentBidPeriod(targetDate);
  if (!period) return "Unknown";
  return `${period.name} ${period.year}`;
};

/**
 * Get the month_ref format used in database (e.g., "January" or "Jan 2026")
 * Tries multiple formats that might be stored in the database
 * @param {Date} targetDate - Optional date (defaults to now)
 * @returns {Array<string>} Array of possible month_ref formats to match against
 */
export const getBidPeriodMonthRefs = (targetDate = new Date()) => {
  const period = getCurrentBidPeriod(targetDate);
  if (!period) return [];
  
  const shortMonth = period.name.substring(0, 3);
  
  return [
    period.name,                              // "January"
    `${period.name} ${period.year}`,          // "January 2026"
    `${shortMonth} ${period.year}`,           // "Jan 2026"
    `${period.year}-${String(period.start.getMonth() + 1).padStart(2, '0')}`, // "2026-01"
    shortMonth,                               // "Jan"
  ];
};

/**
 * Check if a date string falls within a bid period
 * @param {string} dateStr - ISO date string
 * @param {string} periodName - Bid period name (e.g., "January")
 * @param {number} year - Year
 * @returns {boolean}
 */
export const isDateInBidPeriod = (dateStr, periodName, year) => {
  if (!dateStr) return false;
  
  const date = new Date(dateStr);
  const periods = getBidPeriods(year);
  const period = periods[periodName];
  
  if (!period) return false;
  
  return date >= period.start && date <= period.end;
};

/**
 * Get bid period date range formatted for display
 * @param {Date} targetDate - Optional date (defaults to now)
 * @returns {string} e.g., "Jan 1 - Jan 30, 2026"
 */
export const getBidPeriodDateRange = (targetDate = new Date()) => {
  const period = getCurrentBidPeriod(targetDate);
  if (!period) return "";
  
  const formatDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${formatDate(period.start)} - ${formatDate(period.end)}, ${period.year}`;
};

export default {
  getBidPeriods,
  getCurrentBidPeriod,
  getCurrentBidPeriodName,
  getBidPeriodMonthRefs,
  isDateInBidPeriod,
  getBidPeriodDateRange,
};
